// /src/utils/fomoMessageGenerator.ts

export interface FirmForFomo {
  id: string;
  name: string;
  evaluation?: {
    fee?: number;
    discount?: Array<{
      code?: string | null;
      discountedPrice?: number;
      percent?: number | null;
    }>;
  };
  discountPercent?: number | null;
  type?: 'discount' | 'social';
}

const countries = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany",
  "Malaysia", "India", "Singapore", "South Africa", "Brazil",
  "Philippines", "United Arab Emirates", "France", "Netherlands"
];

const countryNames = {
  "United States": ["Jason", "Emily", "Michael", "Jessica", "Matthew", "Ashley", "Joshua", "Sarah", "Daniel", "Hannah"],
  "United Kingdom": ["Oliver", "Amelia", "George", "Isla", "Harry", "Ava", "Noah", "Freddie", "Jack", "Poppy"],
  "Canada": ["Liam", "Emma", "Benjamin", "Sophia", "Lucas", "Charlotte", "Logan", "Mia", "Jacob", "Ella"],
  "Australia": ["Oliver", "Charlotte", "William", "Olivia", "Jack", "Amelia", "Noah", "Isla", "Leo", "Ava"],
  "Germany": ["Ben", "Mia", "Paul", "Emma", "Finn", "Hannah", "Leon", "Sophia", "Luis", "Anna"],
  "Malaysia": ["Ahmad", "Nur", "Muhammad", "Siti", "Adam", "Alya", "Daniel", "Farah", "Zaid", "Aminah"],
  "India": ["Aarav", "Saanvi", "Vivaan", "Ananya", "Aditya", "Isha", "Aryan", "Diya", "Krishna", "Priya"],
  "Singapore": ["Jayden", "Chloe", "Ethan", "Charlotte", "Lucas", "Isabelle", "Ryan", "Emma", "Aiden", "Sophie"],
  "South Africa": ["Ethan", "Emily", "Liam", "Olivia", "Noah", "Isabella", "Jayden", "Ava", "Luke", "Mia"],
  "Brazil": ["Miguel", "Helena", "Arthur", "Alice", "Heitor", "Laura", "Theo", "Valentina", "Davi", "Sophia"],
  "Philippines": ["Joshua", "Angel", "John", "Jasmine", "Mark", "Sophia", "Christian", "Nicole", "Gabriel", "Angelica"],
  "United Arab Emirates": ["Mohammed", "Fatima", "Omar", "Aisha", "Ali", "Maryam", "Ahmed", "Sara", "Hamad", "Noor"],
  "France": ["Lucas", "Emma", "Nathan", "Jade", "Enzo", "Chloé", "Leo", "Louise", "Gabriel", "Manon"],
  "Netherlands": ["Daan", "Emma", "Lucas", "Julia", "Levi", "Mila", "Sem", "Tess", "Finn", "Sophie"]
};

const accountSizes = ["$10K", "$25K", "$50K", "$100K", "$200K"];

function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function isWeekend() {
  const today = new Date().getDay();
  return today === 6 || today === 0; // Saturday or Sunday
}

export function getFomoDelay() {
  const hour = new Date().getHours();

  if (hour >= 8 && hour < 20) {
    return Math.random() * (90000 - 30000) + 30000; // 30s to 90s
  } else if (hour >= 20 || hour < 2) {
    return Math.random() * (120000 - 60000) + 60000; // 60s to 120s
  } else {
    return Math.random() * (240000 - 120000) + 120000; // 2m to 4m
  }
}

export function generateFomoMessage(firm: FirmForFomo) {
  const country = getRandomItem(countries);
  const name = getRandomItem(countryNames[country as keyof typeof countryNames]);
  const firmName = firm.name;
  const firmId = firm.id;
  const fee = Number(firm.evaluation?.fee) || 0;
  const discounted = Number(firm.evaluation?.discount?.[0]?.discountedPrice ?? fee);
  const saved = Math.max(fee - discounted, 0);
  const code = firm.evaluation?.discount?.[0]?.code || "";
  const discountPercent = firm.discountPercent || null;
  const accountSize = getRandomItem(accountSizes);
  const type = firm.type || 'discount';

  let templates;

  if (type === 'discount' && saved > 0) {
    templates = [
      discountPercent
        ? `${name} from ${country} just got ${discountPercent}% off ${firmName}'s ${accountSize} challenge using promo code ${code}.`
        : `${name} from ${country} just got $${saved} off ${firmName}'s ${accountSize} challenge using promo code ${code}.`,
      `${name} saved $${saved} with ${firmName}'s promo code ${code} just now.`,
      `${firmName} discount used! ${name} from ${country} saved $${saved}.`,
      `Someone just joined ${firmName} using promo ${code} — saved $${saved}.`,
      `${firmName}'s ${accountSize} challenge claimed with $${saved} off — promo ${code}.`,
      `🔥 Promo ${code} used on ${firmName}: $${saved} saved just now.`,
      `${name} from ${country} picked ${firmName} and saved $${saved} with ${code}.`,
      `Live deal: ${firmName} promo ${code} — $${saved} off applied.`,
      `New signup alert: ${firmName}, $${saved} discount via ${code}.`,
      `Just in: ${name} scored ${firmName}'s challenge for $${discounted} (was $${fee}).`,
      `${name} from ${country} just secured a ${accountSize} account at ${firmName} — and saved $${saved} with promo code ${code}.`,
      `${name} from ${country} joined ${firmName} with $${saved} off. Ready to pass the challenge!`,
      `${firmName} promo ${code} just used! ${name} from ${country} saved $${saved}.`,
      `${name} activated ${firmName}'s offer — $${saved} off a ${accountSize} funded account.`,
      `A new trader from ${country} saved $${saved} signing up with ${firmName}.`,
      `🔥 ${firmName} discount spotted — ${name} saved $${saved} just now.`,
      `${name} from ${country} chose ${firmName} and claimed $${saved} savings with code ${code}.`
    ];

    if (isWeekend()) {
      templates.push(
        `${name} from ${country} just grabbed a ${accountSize} account at ${firmName} during the weekend rush!`,
        `🚀 Weekend signup: ${name} from ${country} joined ${firmName} just now.`,
        `Weekend warriors: ${name} just started their ${firmName} challenge!`
      );
    }
  } else if (type === 'social') {
    templates = [
      `${name} from ${country} just passed their ${firmName} challenge! 🎉`,
      `New funded trader alert: ${name} from ${country} just got funded with ${firmName}!`,
      `Success story: ${name} from ${country} just received their first payout from ${firmName}.`,
      `Breaking: ${name} from ${country} just completed their ${firmName} evaluation!`,
      `Funded trader milestone: ${name} from ${country} just got their ${accountSize} account with ${firmName}.`,
      `Trading success: ${name} from ${country} just passed their ${firmName} challenge!`,
      `New funded trader: ${name} from ${country} just got their ${accountSize} account with ${firmName}.`,
      `Success alert: ${name} from ${country} just completed their ${firmName} evaluation!`,
      `Funded trader update: ${name} from ${country} just got their first payout from ${firmName}.`,
      `Breaking news: ${name} from ${country} just passed their ${firmName} challenge!`,
      `Trading milestone: ${name} from ${country} just got funded with ${firmName}!`,
      `Success story: ${name} from ${country} just completed their ${firmName} evaluation.`,
      `Funded trader alert: ${name} from ${country} just got their ${accountSize} account with ${firmName}.`,
      `Trading success: ${name} from ${country} just received their first payout from ${firmName}.`,
      `New funded trader: ${name} from ${country} just passed their ${firmName} challenge!`,
      `Success alert: ${name} from ${country} just got funded with ${firmName}!`,
      `Funded trader update: ${name} from ${country} just completed their ${firmName} evaluation.`
    ];
  } else {
    templates = [
      `${name} from ${country} just activated a ${firmName} promo code.`,
      `Someone from ${country} used a code to join ${firmName}.`,
      `Live signup: ${firmName}, new trader joined using promo ${code}.`,
      `${name} is checking out ${firmName} right now.`,
      `${name} from ${country} just started their ${firmName} journey.`,
      `${firmName} challenge signup from ${country} — the next funded trader?`,
      `Another trader from ${country} is checking out ${firmName} right now.`,
      `${name} just picked ${firmName} for their trading journey.`,
      `${firmName} got a new signup from ${country} — let's go!`
    ];

    if (isWeekend()) {
      templates.push(
        `${name} from ${country} just activated a ${firmName} promo code during the weekend rush!`,
        `🚀 Weekend signup: ${name} from ${country} started their journey at ${firmName}.`
      );
    }
  }

  const text = getRandomItem(templates);
  const link = `/go/${firmId}`;

  return { text, link };
}
